// One depth layer of background stars. The field wraps sideways, so the
// layer draws a window of one-screen tiles around the view; the motion loop
// moves the layer and tells it when the window should slide.
import { memo, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { tileSpan, wrapIndex } from './drift';
import type { SkyMotion } from './skyMotion';
import { FIELD_HEIGHT, starTiles, TILE_COUNT, TILE_WIDTH, type BackgroundStar, type DepthLayer } from './weekdaySkies';

interface Props {
  motion: SkyMotion;
  depth: DepthLayer;
  stars: readonly BackgroundStar[];
  /** Pixels per sky unit. */
  scale: number;
  /** The view's width, sky units. */
  visibleWidth: number;
  /** Share of each star's radius drawn on this screen (viewport.ts, starSize). */
  size: number;
}

export const StarLayer = memo(function StarLayer({ motion, depth, stars, scale, visibleWidth, size }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const tiles = useMemo(() => starTiles(stars as BackgroundStar[], depth), [stars, depth]);
  const [first, setFirst] = useState(0);
  useLayoutEffect(() => motion.addLayer(ref.current!, depth, setFirst), [motion, depth]);
  const span = tileSpan(visibleWidth, TILE_WIDTH);
  return (
    <div ref={ref} className="sky-layer">
      {Array.from({ length: span }, (_, i) => first + i).map((k) => (
        <StarTile key={k} k={k} stars={tiles[wrapIndex(k, TILE_COUNT)]!} scale={scale} size={size} />
      ))}
    </div>
  );
});

const StarTile = memo(function StarTile({ k, stars, scale, size }: { k: number; stars: BackgroundStar[]; scale: number; size: number }) {
  const origin = wrapIndex(k, TILE_COUNT) * TILE_WIDTH;
  return (
    <svg className="sky-tile" style={{ left: k * TILE_WIDTH * scale, width: TILE_WIDTH * scale, height: FIELD_HEIGHT * scale }}>
      {stars.map((s) => (
        <circle
          key={s.id}
          data-star={s.id}
          cx={(s.x - origin) * scale}
          cy={s.y * scale}
          r={s.r * size}
          fill={s.cool ? 'var(--star-cool)' : 'var(--star-white)'}
          opacity={s.opacity}
        />
      ))}
    </svg>
  );
});
